<template>
  <div>
    <lb-drawer
      :value="value"
      :heading="isEdit ? 'Edit Request' : 'Create New Request'"
      :loading="loading"
      @input="$emit('input', $event)"
      data-testid="request-form-drawer"
    >
    <template v-slot:body>
      <v-row>
        <v-col cols="12" class="my-0 py-1">
          <p class="ma-0 gray--text">Requested Information*</p>
          <lb-string
            :value="request.description"
            @input="updateField('description', $event)"
            label=""
            placeholder="Enter Requested Information"
            :error="errors.description"
            data-testid="request-form__description"
          />
        </v-col>

        <v-col cols="5" class="my-0 py-1">
          <p class="ma-0 gray--text">Requested from*</p>
          <lb-dropdown
            class="flex-grow-1"
            :items="formattedInfoOwnerList"
            itemtext="displayName"
            itemvalue="_id"
            :value="request.info_owner"
            @input="updateField('info_owner', $event)"
            label=""
            placeholder="Information owner"
            :error="errors.info_owner"
            data-testid="request-form__info-owner"
          />
        </v-col>
        <v-col cols="7" class="my-0 py-1">
          <v-btn
            outlined
            width="20vh"
            text
            class="ml-2 mt-5 shadow-off btnsize"
            :disabled="!hasInfoOwnerSelected"
            @click="editInfoOwner"
            data-testid="request-form__edit-owner-btn"
          >
            <span>Edit Selected</span>
          </v-btn>
          <v-btn
            outlined
            width="20vh"
            text
            class="ml-2 mt-5 shadow-off btnsize"
            @click="createInfoOwner"
            data-testid="request-form__create-owner-btn"
          >
            <span>Create New</span>
          </v-btn>
        </v-col>

        <v-col cols="6" class="my-0 py-1">
          <p class="ma-0 gray--text">Type*</p>
          <lb-dropdown
            class="flex-grow-1"
            :items="typeList"
            :value="request.type"
            @input="updateField('type', $event)"
            label=""
            :error="errors.type"
            itemtext="name"
            itemvalue="value"
            data-testid="request-form__type"
          />
        </v-col>

        <v-col cols="6" class="my-0 py-1">
          <p class="ma-0 gray--text">Priority*</p>
          <lb-dropdown
            class="flex-grow-1"
            :items="priorityList"
            :value="request.priority"
            @input="updateField('priority', $event)"
            label=""
            :error="errors.priority"
            data-testid="request-form__priority"
          />
        </v-col>

        <v-col cols="6" class="my-0 py-1">
          <p class="ma-0 gray--text">Classification*</p>
          <lb-dropdown
            class="flex-grow-1"
            :items="categoryList"
            :value="request.category"
            @input="updateField('category', $event)"
            label=""
            :error="errors.category"
            data-testid="request-form__category"
          />
        </v-col>

        <v-col cols="6" class="my-0 py-1">
          <p class="ma-0 gray--text">Date*</p>
          <lb-date
            :value="request.duedate"
            @input="updateField('duedate', $event)"
            label="Due Date"
            :error="errors.duedate"
            :min="minDate"
            data-testid="request-form__duedate"
          />
        </v-col>

        <v-col cols="6" class="my-0 py-1">
          <p class="ma-0 gray--text">{{ locationHeader }}*</p>
          <lb-dropdown
            class="flex-grow-1"
            :items="locationList"
            itemtext="name"
            itemvalue="_id"
            :value="request.location"
            @input="updateField('location', $event)"
            label=""
            :error="errors.location"
            data-testid="request-form__location"
          />
        </v-col>

        <v-col cols="12" class="my-0 py-1" v-if="request.type !== 'text'">
          <p class="ma-0 gray--text">Link For File uploaded(optional)</p>
          <lb-string
            :value="request.uploadlink"
            @input="updateField('uploadlink', $event)"
            label=""
            placeholder="Upload Link"
            :error="errors.uploadlink"
            data-testid="request-form__uploadlink"
          />
        </v-col>

        <v-col cols="12">
          <span class="font-weight-semibold">Select Tasks:</span>
          <br />
          <div class="align-center justify-start">
            <div class="align-center justify-start">
              <v-chip
                v-for="item in selectedTaskItems"
                :key="item.value"
                label
                class="caccounting--text lightpurple ma-1"
                close
                @click:close="removeTask(item.value)"
                data-testid="request-form__task-chip"
              >
                <span>{{ item.ref }}</span>
              </v-chip>
            </div>
          </div>
        </v-col>

        <v-col cols="12">
          <v-menu :close-on-content-click="false" offset-y transition="slide-y-transition" ref="taskListMenu">
            <template v-slot:activator="{ on, attrs }">
              <v-btn text v-bind="attrs" v-on="on" class="all-width align-center border-on justify-space-between">
                <span>Select Tasks</span>
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-list class="ma-0 d-flex flex-column align-start justify-start all-width pa-2">
              <div class="" style="max-height: 45vh; min-width: 100%; overflow: auto;z-index: 13 !important;width: 20vh;" @scroll="onTaskScroll" ref="taskScrollContainer">
                <lb-string
                  label="Search"
                  class="all-width"
                  hidedetails
                  :value="taskSearchQuery"
                  @input="onTaskSearch"
                  placeholder="Search tasks..."
                  data-testid="request-form__task-search"
                />
                <v-simple-table dense class="all-width mt-2 border-on radius-small" data-testid="request-form__task-table">
                  <thead class="tableHeaderBg">
                    <tr>
                      <th class="gray-background">
                        <v-checkbox class="ma-0 pa-0" hide-details color="caccounting"
                          :input-value="allTasksSelected"
                          @change="toggleSelectAll"
                          data-testid="request-form__toggle-all"
                        ></v-checkbox>
                      </th>
                      
                      <th class="gray-background" scope="ref">Ref</th>
                      <th class="gray-background" scope="location">
                        {{ $store.state.LocationHeader || "Location" }}
                      </th>
                      <th class="gray-background" scope="columns"> Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(account, idx) in filteredTaskList">
                      <tr :key="account.value || idx" data-testid="request-form__task-row">
                        <td>
                          <v-checkbox class="mt-0" dense label="" hide-details
                            :input-value="internalSelectedTasks.includes(account.value)"
                            @change="toggleTask(account.value)"
                            data-testid="request-form__task-checkbox"
                          ></v-checkbox>
                        </td>
                        <td>{{ account.ref }}</td>
                        <td>{{ getLocationName(account.location) }}</td>
                        <td>{{ account.name }}</td>
                      </tr>
                    </template>
                    <tr v-if="taskLoading" data-testid="request-form__task-loading">
                      <td colspan="4" class="text-center pa-2">
                        <v-progress-circular indeterminate size="20" width="2" color="caccounting"></v-progress-circular>
                        <span class="ml-2 caption">Loading...</span>
                      </td>
                    </tr>
                    <tr v-if="!taskLoading && filteredTaskList.length === 0" data-testid="request-form__task-empty">
                      <td colspan="4" class="text-center pa-2 grey--text">No tasks found</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
              <div class="d-flex align-center all-width justify-end mt-2">
                <v-btn class="ml-2 white--text" color="primary" @click="$refs.taskListMenu.isActive = false;" data-testid="request-form__task-select"><span>Select</span></v-btn>
              </div>
            </v-list>
          </v-menu>
        </v-col>

        <v-col cols="12" class="my-0 py-1">
          <v-simple-table dense class="mb-4 border-on radius-small" v-if="selectedTaskItems.length > 0" data-testid="request-form__selected-task-table">
            <thead class="tableHeaderBg">
              <tr>
                <th class="tableHeaderBg black--text" scope="Red">Ref</th>
                <th class="tableHeaderBg black--text" scope="location">Location</th>
                <th class="tableHeaderBg black--text" scope="Name">Task Name</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in selectedTaskItems" :key="item.value" data-testid="request-form__selected-task-row">
                <td class="">{{ item.ref }}</td>
                <td class="">{{ getLocationName(item?.location) }}</td>
                <td>{{ item.name }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <div v-else class="grey--text title d-flex align-center justify-center">
            <span>No selected task available</span>
          </div>
        </v-col>
      </v-row>
    </template>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn
        v-if="isEdit"
        color="primary"
        class="white--text"
        @click="handleUpdate"
        data-testid="request-form__update-btn"
      >
        <span>Update</span>
      </v-btn>
      <v-btn
        v-else
        color="primary"
        class="white--text"
        @click="$emit('create')"
        data-testid="request-form__create-btn"
      >
        <span>Create</span>
      </v-btn>
    </template>
  </lb-drawer>

  <!-- Info Owner Dialog (outside main drawer to avoid z-index issues) -->
  <InfoOwnerDrawer
    v-model="infoOwnerDialog"
    :info-owner="infoOwnerForm"
    :errors="infoOwnerErrors"
    :loading="infoOwnerLoading"
    @update-field="updateInfoOwnerField"
    @update="handleUpdateOwner"
    @add="handleAddOwner"
  />
  </div>
</template>

<script>
import InfoOwnerDrawer from './InfoOwnerDrawer.vue';

export default {
  name: 'RequestFormDrawer',
  components: {
    InfoOwnerDrawer,
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    request: {
      type: Object,
      default: () => ({}),
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    infoOwnerList: {
      type: Array,
      default: () => [],
    },
    typeList: {
      type: Array,
      default: () => [],
    },
    priorityList: {
      type: Array,
      default: () => [],
    },
    categoryList: {
      type: Array,
      default: () => [],
    },
    locationList: {
      type: Array,
      default: () => [],
    },
    locationHeader: {
      type: String,
      default: 'Location',
    },
    minDate: {
      type: String,
      default: '',
    },
    selectedTasks: {
      type: Array,
      default: () => [],
    },
    taskOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      internalSelectedTasks: [],
      taskSearchQuery: '',
      infoOwnerDialog: false,
      infoOwnerLoading: false,
      infoOwnerForm: {},
      infoOwnerErrors: {},
      // Lazy task loading state
      taskRecords: [],
      taskPage: 1,
      taskLimit: 50,
      taskTotalCount: 0,
      taskLoading: false,
      taskFullyLoaded: false,
      taskSearchTimer: null,
      taskInitialized: false,
    };
  },
  computed: {
    isEdit() {
      return !!this.request._id;
    },
    selectedTaskItems() {
      // Build from both taskRecords and previously selected items in taskOptions fallback
      const recordMap = new Map(this.taskRecords.map(r => [r._id, r]));
      const optionMap = new Map((this.taskOptions || []).map(item => [item.value, item]));
      return this.internalSelectedTasks
        .map(value => {
          const record = recordMap.get(value);
          if (record) return { value: record._id, name: record.description, ref: record.ref, location: record.location };
          return optionMap.get(value);
        })
        .filter(Boolean);
    },
    filteredTaskList() {
      // Map lazy-loaded records to the shape the template expects
      return this.taskRecords.map(task => ({
        value: task._id,
        name: task.description,
        ref: task.ref,
        location: task.location,
        master_id: task.master_id,
      }));
    },
    allTasksSelected() {
      return (
        this.filteredTaskList.length > 0 &&
        this.filteredTaskList.every(option => this.internalSelectedTasks.includes(option.value))
      );
    },
    formattedInfoOwnerList() {
      return (this.infoOwnerList || []).map(owner => ({
        ...owner,
        displayName: owner.email ? `${owner.name} (${owner.email})` : owner.name,
      }));
    },
    hasInfoOwnerSelected() {
      return !!(this.request.info_owner && this.request.info_owner.trim && this.request.info_owner.trim() !== '');
    },
  },
  watch: {
    // Keep internal selection aligned with parent-provided value
    selectedTasks: {
      immediate: true,
      handler(newValue) {
        this.internalSelectedTasks = Array.isArray(newValue) ? [...new Set(newValue)] : [];
      },
    },
    // Load task data when drawer opens
    value(isOpen) {
      if (isOpen && !this.taskInitialized) {
        this.loadTaskFirstPage();
      }
    },
    // Reload tasks filtered by location when location changes
    'request.location'(newLocationId) {
      if (this.value) {
        this.taskInitialized = false;
        this.loadTaskFirstPage(newLocationId);
      }
    },
  },
  beforeDestroy() {
    if (this.taskSearchTimer) {
      clearTimeout(this.taskSearchTimer);
      this.taskSearchTimer = null;
    }
  },
  methods: {
    updateField(field, value) {
      this.$emit('update-field', { field, value });
    },
    getLocationName(locationId) {
      if (!locationId) return '';
      const location = this.locationList.find(loc => loc._id === locationId);
      return location?.name || '';
    },
    closeTaskMenu() {
      if (this.$refs.taskListMenu) {
        this.$refs.taskListMenu.isActive = false;
      }
    },
    emitSelectedTasks() {
      const uniqueSelection = [...new Set(this.internalSelectedTasks)];
      this.internalSelectedTasks = uniqueSelection;
      this.$emit('update-selected-tasks', uniqueSelection);
    },
    // --- Lazy task loading methods ---
    async loadTaskFirstPage(locationId) {
      const projectId = this.$route.params.id;
      if (!projectId) return;
      this.taskPage = 1;
      this.taskLoading = true;
      this.taskFullyLoaded = false;
      const effectiveLocationId = locationId || this.request.location || undefined;
      try {
        const result = await this.$store.dispatch('informationManager/fetchChecklistPage', {
          projectId,
          pagination: { page: 1, limit: this.taskLimit },
          search: this.taskSearchQuery || undefined,
          searchFields: ['ref', 'description', 'locationName'],
          locationId: effectiveLocationId,
        });
        this.taskRecords = result.records || [];
        this.taskTotalCount = result.totalCount || 0;
        this.taskFullyLoaded = this.taskRecords.length >= this.taskTotalCount;
        this.taskInitialized = true;
      } catch (err) {
        console.error('[RequestFormDrawer]: loadTaskFirstPage error', err);
        this.taskRecords = [];
      } finally {
        this.taskLoading = false;
      }
    },
    async loadNextTaskPage() {
      if (this.taskLoading || this.taskFullyLoaded) return;
      const projectId = this.$route.params.id;
      if (!projectId) return;
      this.taskPage += 1;
      this.taskLoading = true;
      try {
        const result = await this.$store.dispatch('informationManager/fetchChecklistPage', {
          projectId,
          pagination: { page: this.taskPage, limit: this.taskLimit },
          search: this.taskSearchQuery || undefined,
          searchFields: ['ref', 'description', 'locationName'],
          locationId: this.request.location || undefined,
        });
        const newRecords = result.records || [];
        this.taskRecords = [...this.taskRecords, ...newRecords];
        this.taskTotalCount = result.totalCount || 0;
        this.taskFullyLoaded = this.taskRecords.length >= this.taskTotalCount;
      } catch (err) {
        console.error('[RequestFormDrawer]: loadNextTaskPage error', err);
      } finally {
        this.taskLoading = false;
      }
    },
    onTaskSearch(value) {
      const normalizedQuery = value || '';
      if (normalizedQuery === this.taskSearchQuery) {
        return;
      }
      this.taskSearchQuery = normalizedQuery;
      if (this.taskSearchTimer) clearTimeout(this.taskSearchTimer);
      this.taskSearchTimer = setTimeout(() => {
        this.loadTaskFirstPage(this.request.location || undefined);
      }, 300);
    },
    onTaskScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        this.loadNextTaskPage();
      }
    },
    // --- Selection methods ---
    toggleSelectAll(isSelected) {
      const shouldSelect = typeof isSelected === 'boolean' ? isSelected : !this.allTasksSelected;
      if (shouldSelect) {
        const merged = new Set([
          ...this.internalSelectedTasks,
          ...this.filteredTaskList.map(option => option.value),
        ]);
        this.internalSelectedTasks = Array.from(merged);
      } else {
        const filteredValues = new Set(this.filteredTaskList.map(option => option.value));
        this.internalSelectedTasks = this.internalSelectedTasks.filter(value => !filteredValues.has(value));
      }
      this.emitSelectedTasks();
    },
    toggleTask(value) {
      if (this.internalSelectedTasks.includes(value)) {
        this.internalSelectedTasks = this.internalSelectedTasks.filter(item => item !== value);
      } else {
        this.internalSelectedTasks = [...this.internalSelectedTasks, value];
      }
      this.emitSelectedTasks();
    },
    removeTask(value) {
      this.internalSelectedTasks = this.internalSelectedTasks.filter(item => item !== value);
      this.emitSelectedTasks();
    },
    handleUpdate() {
      // Defensive: Validate that all selected tasks belong to the selected location and gracefully handle weird edge cases
      if (this.request.location && Array.isArray(this.selectedTaskItems) && this.selectedTaskItems.length > 0) {
        const mismatchedTasks = this.selectedTaskItems.filter(item => {
          // Accept items where location is empty/null/undefined (allow blanks), but flag if it's different and present
          if (item && Object.prototype.hasOwnProperty.call(item, 'location')) {
            const itemLoc = item.location || '';
            return itemLoc && itemLoc !== this.request.location;
          }
          return false;
        });
        if (mismatchedTasks.length > 0) {
          const locationName = this.getLocationName(this.request.location) || 'the selected location';
          this.$store.commit(
            'sbError',
            `Some selected tasks do not belong to "${locationName}". Please remove tasks from other locations before updating.`,
            { root: true }
          );
          return;
        }
      }
      this.$emit('update');
    },
    /**
     * Updates info owner field value
     */
    updateInfoOwnerField(payload) {
      this.$set(this.infoOwnerForm, payload.field, payload.value);
    },
    /**
     * Opens dialog to create a new info owner
     */
    createInfoOwner() {
      this.infoOwnerForm = {};
      this.infoOwnerErrors = {};
      this.infoOwnerDialog = true;
    },
    /**
     * Opens dialog to edit existing info owner
     */
    editInfoOwner() {
      if (!this.request.info_owner) return;
      const owner = this.infoOwnerList.find(o => o._id === this.request.info_owner);
      if (owner) {
        this.infoOwnerForm = { ...owner };
        this.infoOwnerErrors = {};
        this.infoOwnerDialog = true;
      }
    },
    /**
     * Handles adding new info owner
     */
    async handleAddOwner() {
      this.infoOwnerLoading = true;
      try {
        const response = await this.$store.dispatch('informationManager/addInfoOwner', { 
          data: this.infoOwnerForm 
        });
        if (response && response.data && response.data[0]) {
          const newOwner = response.data[0];
          this.infoOwnerDialog = false;
          this.infoOwnerForm = {};
          // Wait for next tick to ensure infoOwnerList is updated with new owner
          await this.$nextTick();
          this.updateField('info_owner', newOwner._id);
        } else if (response.status === 'error' && response.data) {
          if (response.data[0] && response.data[0].index0) {
            this.infoOwnerErrors = response.data[0].index0;
          }
        }
      } catch (err) {
        console.error('[RequestFormDrawer]: handleAddOwner error', err);
      } finally {
        this.infoOwnerLoading = false;
      }
    },
    /**
     * Handles updating existing info owner
     */
    async handleUpdateOwner() {
      this.infoOwnerLoading = true;
      const ownerId = this.infoOwnerForm._id; // Preserve the owner ID
      try {
        const response = await this.$store.dispatch('informationManager/editInfoOwner', { 
          id: this.infoOwnerForm._id, 
          data: this.infoOwnerForm 
        });
        if (response && response.status !== 'error') {
          this.infoOwnerDialog = false;
          this.infoOwnerForm = {};
          // Force dropdown to re-render with updated value by clearing and resetting
          await this.$nextTick();
          this.updateField('info_owner', null);
          await this.$nextTick();
          this.updateField('info_owner', ownerId);
        } else if (response.status === 'error' && response.data) {
          if (response.data[0] && response.data[0].index0) {
            this.infoOwnerErrors = response.data[0].index0;
          }
        }
      } catch (err) {
        console.error('[RequestFormDrawer]: handleUpdateOwner error', err);
      } finally {
        this.infoOwnerLoading = false;
      }
    },
  },
};
</script>
